import React, { useEffect, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
    Box,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    MenuItem,
    Select
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import db from '../firebase';
import { format, parseISO, startOfWeek } from 'date-fns';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const ArticleCountGraph = ({refreshTrigger}) => {
    const [rawCounts, setRawCounts] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [view, setView] = useState('daily');

    useEffect(() => {
        const fetchData = async () => {
            const docRef = doc(db, 'metadata', 'articleCounts');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const counts = docSnap.data().dailyCounts;
                const sorted = counts.sort((a, b) => parseISO(a.date) - parseISO(b.date));
                setRawCounts(sorted);
            }
        };

        fetchData();
    }, [refreshTrigger]);

    useEffect(() => {
        if (!rawCounts.length) return;

        let labels = [];
        let data = [];

        if (view === 'daily') {
            labels = rawCounts.map(item => format(parseISO(item.date), 'dd MMM'));
            data = rawCounts.map(item => item.count);
        }

        if (view === 'cumulative') {
            let total = 0;
            labels = rawCounts.map(item => format(parseISO(item.date), 'dd MMM'));
            data = rawCounts.map(item => {
                total += item.count;
                return total;
            });
        }

        if (view === 'weekly') {
            const weeklyMap = {};
            rawCounts.forEach(item => {
                const weekStart = format(startOfWeek(parseISO(item.date)), 'dd MMM');
                weeklyMap[weekStart] = (weeklyMap[weekStart] || 0) + item.count;
            });
            labels = Object.keys(weeklyMap);
            data = Object.values(weeklyMap);
        }

        setChartData({
            labels,
            datasets: [{
                label: view === 'daily'
                    ? 'Articles per Day'
                    : view === 'cumulative'
                    ? 'Cumulative Total'
                    : 'Articles per Week',
                data,
                fill: false,
                borderColor: '#FFA500',
                tension: 0.1,
                pointRadius: 3
            }]
        });

    }, [rawCounts, view]);

    if (!chartData) return null;

    return (
        <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Article Trends</Typography>
                <FormControl size="small">
                    <InputLabel>View</InputLabel>
                    <Select value={view} label="View" onChange={(e) => setView(e.target.value)}>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="cumulative">Cumulative</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ height: 300 }}>
                <Line
                    data={chartData}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: { color: 'black', font: { size: 12 } },
                                grid: { color: 'rgba(0, 0, 0, 0.1)' }
                            },
                            x: {
                                ticks: { color: 'black', font: { size: 10 }, maxRotation: 45, minRotation: 45 },
                                grid: { color: 'rgba(0, 0, 0, 0.1)' }
                            }
                        },
                        plugins: {
                            title: {
                                display: true,
                                text: view === 'daily'
                                    ? 'Articles Per Day'
                                    : view === 'cumulative'
                                    ? 'Cumulative Articles Over Time'
                                    : 'Articles Per Week',
                                color: 'black',
                                font: { size: 16, weight: 'bold' }
                            },
                            legend: {
                                labels: { color: 'black' }
                            },
                            tooltip: {
                                mode: 'index',
                                intersect: false
                            }
                        }
                    }}
                />
            </Box>
        </Paper>
    );
};

export default ArticleCountGraph;
